import React, { useState, useEffect, useMemo } from 'react';
import { useData } from "../backend/firebase";
import { DateTime } from 'luxon';
import MultiStreamPlayer from '../components/MultiStreamPlayer';
import SEO from '../components/SEO';
import { getImagePath } from '../utils/imageRegistry';
import { Link, useLocation } from 'react-router-dom';

const Watch = () => {
    const { schedule, teams, userRole } = useData();
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [showArchives, setShowArchives] = useState(false);

    const [currentTime, setCurrentTime] = useState(DateTime.now());



    // const [showAllResults, setShowAllResults] = useState(false);
    // const [revealedMatches, setRevealedMatches] = useState({});

    const canTest = userRole && ['admin', 'captain', 'commentator'].includes(userRole.role);
    const [testState, setTestState] = useState('normal'); // 'normal', 'live', 'upcoming', 'ended'

    const [testLinks, setTestLinks] = useState(['']);
    const [timeOffset, setTimeOffset] = useState(0);
    const [isLive, setIsLive] = useState(false);
    
    // Replace your old 'now' constant with this derived value
    const now = currentTime.plus({ minutes: timeOffset });
    // const now = DateTime.now().plus({ minutes: timeOffset });
    const getLocalTime = (isoString) => {
        return DateTime.fromISO(isoString, { zone: 'America/Los_Angeles' }).toLocal();
    };

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(DateTime.now());
        }, 1000);

        return () => clearInterval(timer); // Cleanup to prevent memory leaks
    }, []);

    

    // const TeamIcon = ({ teamId, size = 28 }) => {
    //     const team = teams.find(t => t.id.toString() === teamId?.toString());
    //     if (!team) return <div style={{width: size, height: size, background: '#333', borderRadius: '50%'}} />;
        
    //     const src = team.logoUrl ? getImagePath(team.logoUrl) : (team.logo ? `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}` : null);
        
    //     return (
    //         <div style={{ width: size, height: size, borderRadius: '50%', background: team.color || '#333', padding: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
    //             {src ? <img src={src} alt="" style={{width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover'}} /> : null}
    //         </div>
    //     );
    // };


    const TeamDetails = ({ teamId, align = 'left' }) => {
        const team = teams.find(t => t.id.toString() === teamId?.toString());
        if (!team) return <div style={{flex: 1}}></div>;

        const playersStr = Array.isArray(team.players) ? team.players.join(', ') : (team.captain || '');
        const color = team.color || '#6C5DD3';
        const logoSrc = team.logoUrl ? getImagePath(team.logoUrl) : (team.logo ? `data:image/svg+xml;utf8,${encodeURIComponent(team.logo)}` : null);

        const innerHeader = (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexDirection: align === 'right' ? 'row-reverse' : 'row' }}>
                {logoSrc && <img src={logoSrc} alt="" style={{width: 20, height: 20, borderRadius: '50%', objectFit: 'cover', border: `1px solid ${color}`}} />}
                <div style={{ fontSize: 16, fontWeight: '900', color: color, textTransform: 'uppercase', transition: 'opacity 0.2s' }}>
                    {team.name}
                </div>
            </div>
        );

        return (
            <div style={{ 
                flex: 1, display: 'flex', flexDirection: 'column',
                alignItems: align === 'right' ? 'flex-end' : 'flex-start',
                textAlign: align,
                borderLeft: align === 'left' ? `4px solid ${color}` : 'none',
                borderRight: align === 'right' ? `4px solid ${color}` : 'none',
                padding: align === 'left' ? '0 0 0 15px' : '0 15px 0 0',
            }}>
                {team.socialLink ? (
                    <a href={team.socialLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                        {innerHeader}
                    </a>
                ) : innerHeader}
                
                {playersStr && (
                    <div style={{ fontSize: 12, color: '#8F9EB5', marginTop: 4, lineHeight: 1.4 }}>
                        {playersStr}
                    </div>
                )}
            </div>
        );
    };


    const getEffectiveStartTime = (isoString) => {
        const time = getLocalTime(isoString);
        return userRole ? time : time.plus({ minutes: 15 });
    };

    // --- TIME FILTERING ---
    const realLiveEvents = useMemo(() => {
        return schedule.filter(e => {
            const start = getEffectiveStartTime(e.startTime);
            // SECRECY FIX: Subtract 15m to unlock exactly at T+0 for logged-out users
            const liveThreshold = userRole ? start : start.minus({ minutes: 15 });
            const end = e.endTime ? userRole ? getEffectiveStartTime(e.endTime) : getEffectiveStartTime(e.endTime).minus({ minutes: 15 }) : start.plus({ hours: 2, minutes: 30 });
            return now >= liveThreshold && now <= end;
        });
    }, [schedule, currentTime, timeOffset, userRole]); // Added userRole to dependency array
    // const realLiveEvents = useMemo(() => {
    //     return schedule.filter(e => 
    //         getEffectiveStartTime(e.startTime) <= now && 
    //         getEffectiveStartTime(e.endTime) >= now
    //     );
    // }, [schedule, currentTime, timeOffset]);

    const realUpcomingEvents = useMemo(() => {
        return schedule
            .filter(e => {
                const start = getEffectiveStartTime(e.startTime);
                const liveThreshold = userRole ? start : start.minus({ minutes: 15 });
                return now < liveThreshold;
            })
            .sort((a, b) => getEffectiveStartTime(a.startTime) - getEffectiveStartTime(b.startTime));
    }, [schedule, currentTime, timeOffset, userRole]);

    const realPastEvents = useMemo(() => {
        return schedule
            .filter(e => {
                const start = getEffectiveStartTime(e.startTime);
                const end = e.endTime ? getEffectiveStartTime(e.endTime) : start.plus({ hours: 2, minutes: 30 });
                return now > end;
            })
            .sort((a, b) => getLocalTime(b.startTime) - getLocalTime(a.startTime));
    }, [schedule, currentTime, timeOffset]);

    // const realUpcomingEvents = useMemo(() => {
    //     return schedule
    //         .filter(e => getEffectiveStartTime(e.startTime) > now)
    //         .sort((a, b) => getEffectiveStartTime(a.startTime) - getEffectiveStartTime(b.startTime));
    // }, [schedule, currentTime, timeOffset]);

    // const realPastEvents = useMemo(() => {
    //     return schedule
    //         .filter(e => getEffectiveStartTime(e.endTime) < now)
    //         .sort((a, b) => getLocalTime(b.startTime) - getLocalTime(a.startTime));
    // }, [schedule, currentTime, timeOffset]);

    // 2. Assign them to mutable 'let' variables so the Simulator can overwrite them later
    let liveEvents = realLiveEvents;
    let upcomingEvents = realUpcomingEvents;
    let pastEvents = realPastEvents;

    // --- SIMULATOR OVERRIDES ---
    if (canTest && testState !== 'normal') {
        
        // Dynamically build the links array, filtering out empty inputs
        const dummyLinks = testLinks
            .filter(url => url.trim() !== '')
            .map((url, index) => {
                let label = `Extra Stream ${index - 2}`;
                if (index === 0) label = 'Commentary';
                else if (index === 1) label = 'Captain 1';
                else if (index === 2) label = 'Captain 2';
                
                return {
                    url: url.trim(),
                    label: label,
                    teamId: index === 1 && teams.length > 0 ? teams[0].id : (index === 2 && teams.length > 1 ? teams[1].id : null)
                };
            });

        const dummyEvent = {
            id: 'test-simulator-event',
            title: '🧪 SIMULATED BROADCAST',
            startTime: testState === 'upcoming' ? now.plus({hours: 2}).toISO() : now.minus({minutes: 30}).toISO(),
            endTime: testState === 'upcoming' ? now.plus({hours: 3}).toISO() : now.plus({minutes: 30}).toISO(),
            team1: teams.length > 0 ? teams[0].id : null,
            team2: teams.length > 1 ? teams[1].id : null,
            links: dummyLinks // Injecting our dynamic array
        };

        if (testState === 'live') {
            liveEvents = [dummyEvent];
            upcomingEvents = [];
        } else if (testState === 'upcoming') {
            liveEvents = [];
            upcomingEvents = [dummyEvent];
        } else if (testState === 'ended') {
            liveEvents = [];
            upcomingEvents = [];
        }
    }


    const liveEventIds = liveEvents.map(e => e.id).join(',');
    const nextEvent = upcomingEvents.length > 0 ? upcomingEvents[0] : null;

    let activePlayerEvent = selectedEvent;
    if (canTest && testState !== 'normal' && selectedEvent?.id === 'test-simulator-event') {
        activePlayerEvent = liveEvents.find(e => e.id === 'test-simulator-event') 
                         || upcomingEvents.find(e => e.id === 'test-simulator-event') 
                         || selectedEvent;
    }

    

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const matchIdFromUrl = queryParams.get('matchId');

    // --- INITIALIZATION & ROUTING ---
    useEffect(() => {
        // 1. URL Parameter Routing (Overrides everything)
        if (matchIdFromUrl && schedule.length > 0) {
            const targetEvent = schedule.find(e => e.id === matchIdFromUrl);
            if (targetEvent && selectedEvent?.id !== targetEvent.id) {
                setShowArchives(true);
                setSelectedEvent(targetEvent);
                // Wait for the DOM to render the archives, then scroll perfectly to it
                setTimeout(() => {
                    const el = document.getElementById(`archive-${matchIdFromUrl}`);
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }, 300);
            }
        } 
        // 2. Normal Logic (Auto-play live, open archives if empty)
        else {
            if (liveEvents.length > 0 && !selectedEvent) {
                setSelectedEvent(liveEvents[0]);
                setIsLive(true);
            }
            if (isLive && liveEvents.length === 0) {
                setSelectedEvent(null);
                setIsLive(false);
            }
            if (liveEvents.length === 0 && upcomingEvents.length === 0) {
                setShowArchives(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [matchIdFromUrl, liveEventIds, testState]);

    // --- HELPER: FORMAT TIME UNTIL NEXT EVENT ---
    const getCountdownText = (isoString) => {
        const start = getEffectiveStartTime(isoString);
        const diff = start.diff(now, ['days', 'hours', 'minutes']).toObject();
        
        const d = Math.floor(diff.days || 0);
        const h = Math.floor(diff.hours || 0);
        const m = Math.floor(diff.minutes || 0);

        // If it's more than 24 hours away, show Days and Hours
        if (d > 0) {
            return `in ${d} day${d > 1 ? 's' : ''}${` ${h} hour(s)`}`;
        }
        
        // If it's under 24 hours, just show Hours (and maybe minutes if you prefer)
        if (h > 0) {
            return `in ${h} hour(s)${` ${m} minutes`}`;
        }
        
        // Under 1 hour
        return `in ${Math.max(1, m)} minutes`;
    };

    // --- HELPER: TEAM DISPLAY FOR ARCHIVES ---
    // const TeamDetails = ({ teamId, align = 'left' }) => {
    //     const team = teams.find(t => t.id.toString() === teamId?.toString());
    //     if (!team) return <div style={{flex: 1}}></div>;

    //     // Safely extract players if it's an array, or fallback to captain
    //     const playersStr = Array.isArray(team.players) ? team.players.join(', ') : (team.captain || '');
    //     const color = team.color || '#6C5DD3';

    //     return (
    //         <div style={{ 
    //             flex: 1, 
    //             display: 'flex', 
    //             flexDirection: 'column',
    //             textAlign: align,
    //             borderLeft: align === 'left' ? `4px solid ${color}` : 'none',
    //             borderRight: align === 'right' ? `4px solid ${color}` : 'none',
    //             padding: align === 'left' ? '0 0 0 15px' : '0 15px 0 0',
    //         }}>
    //             <div style={{ fontSize: 16, fontWeight: '900', color: color, textTransform: 'uppercase' }}>
    //                 {team.name}
    //             </div>
    //             {playersStr && (
    //                 <div style={{ fontSize: 12, color: '#8F9EB5', marginTop: 4, lineHeight: 1.4 }}>
    //                     {playersStr}
    //                 </div>
    //             )}
    //         </div>
    //     );
    // };

    return (
        <div className="PageLayout">
            <SEO title="Watch Live & VODs" description="Watch live matches and past VODs from Bridge Brawl." url="/watch" />
            <div className="ContentWrapper CustomScroll" style={{flex: 1, overflowY: 'auto', padding: '20px'}}>
                
                <div style={{ maxWidth: 1000, margin: '0 auto', width: '100%' }}>

                    {canTest && (
                        <div style={{ background: 'rgba(255, 215, 0, 0.05)', border: '1px solid rgba(255, 215, 0, 0.2)', borderRadius: 8, padding: '15px', marginBottom: 20, display: 'flex', flexDirection: 'column', gap: 15 }}>
                            
                            {/* Simulator Buttons */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                <span style={{ color: '#FFD700', fontWeight: 'bold', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, marginRight: 10 }}>🛠️ Staff Simulator:</span>
                                
                                {[
                                    { id: 'normal', label: 'Live Data' },
                                    { id: 'live', label: 'Force Live' },
                                    { id: 'upcoming', label: 'Force Upcoming' },
                                    { id: 'ended', label: 'Force Ended' }
                                ].map(btn => (
                                    <button 
                                        key={btn.id}
                                        onClick={() => {
                                            setTestState(btn.id);
                                            setSelectedEvent(null); 
                                            setShowArchives(false); 
                                        }}
                                        style={{
                                            background: testState === btn.id ? '#FFD700' : 'rgba(255,255,255,0.05)',
                                            color: testState === btn.id ? '#000' : '#8F9EB5',
                                            border: 'none', padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 'bold', cursor: 'pointer', transition: 'all 0.2s'
                                        }}
                                    >
                                        {btn.label}
                                    </button>
                                ))}
                            </div>
                            <div style={{ borderTop: '1px dashed rgba(255, 215, 0, 0.2)', paddingTop: 15, display: 'flex', flexDirection: 'column', gap: 10 }}>
                                
                                {/* 1. The Dashboard: Shows exactly "When" you are */}
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(0,0,0,0.3)', padding: '8px 12px', borderRadius: 6 }}>
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <span style={{ color: '#8F9EB5', fontSize: 10, fontWeight: 'bold', textTransform: 'uppercase' }}>SIMULATED TIME</span>
                                        <span style={{ color: '#FFD700', fontSize: 14, fontWeight: 'bold' }}>
                                            {now.toFormat('EEE, MMM d @ h:mm a')}
                                        </span>
                                    </div>
                                    <button onClick={() => setTimeOffset(0)} style={{background: 'rgba(255, 92, 92, 0.2)', color: '#FF5C5C', border: '1px solid rgba(255, 92, 92, 0.3)', padding: '4px 10px', borderRadius: 4, fontSize: 10, cursor: 'pointer', fontWeight: 'bold'}}>
                                        ↺ Reset to Now
                                    </button>
                                </div>

                                {/* 2. Macro Controls: Days & Weeks */}
                                <div style={{ display: 'flex', gap: 5, justifyContent: 'center', alignItems: 'center' }}>
                                    <span style={{ color: '#555', fontSize: 10, fontWeight: 'bold', width: 40 }}>MACRO</span>
                                    {[-10080, -1440, 1440, 10080].map(mins => {
                                        const label = Math.abs(mins) === 1440 ? 'Day' : 'Week';
                                        const sign = mins > 0 ? '+' : '-';
                                        return (
                                            <button 
                                                key={mins}
                                                onClick={() => setTimeOffset(prev => prev + mins)}
                                                style={{
                                                    flex: 1,
                                                    background: 'rgba(108, 93, 211, 0.1)', 
                                                    color: '#A395E9', 
                                                    border: '1px solid rgba(108, 93, 211, 0.3)', 
                                                    padding: '6px 0', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontWeight: 'bold'
                                                }}
                                            >
                                                {sign}1 {label}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* 3. Micro Controls: Minutes */}
                                <div style={{ display: 'flex', gap: 5, justifyContent: 'center', alignItems: 'center' }}>
                                    <span style={{ color: '#555', fontSize: 10, fontWeight: 'bold', width: 40 }}>MICRO</span>
                                    {[-60, -15, -5, 5, 15, 60].map(mins => (
                                        <button 
                                            key={mins}
                                            onClick={() => setTimeOffset(prev => prev + mins)}
                                            style={{
                                                flex: 1,
                                                background: 'rgba(255, 255, 255, 0.05)', 
                                                color: '#ccc', 
                                                border: '1px solid rgba(255, 255, 255, 0.1)', 
                                                padding: '4px 0', borderRadius: 4, fontSize: 10, cursor: 'pointer'
                                            }}
                                        >
                                            {mins > 0 ? '+' : ''}{mins}m
                                        </button>
                                    ))}
                                </div>
                                
                                {/* 4. The "Fine Tune" Slider (Range: +/- 24 hours from current offset) */}
                                <input 
                                    type="range" 
                                    min={timeOffset - 1440} max={timeOffset + 1440} step="15" 
                                    value={timeOffset} 
                                    onChange={(e) => setTimeOffset(parseInt(e.target.value))}
                                    style={{ width: '100%', accentColor: '#FFD700', cursor: 'pointer', marginTop: 5 }}
                                />
                            </div>

                            {testState !== 'normal' && (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: '1px dashed rgba(255, 215, 0, 0.2)', paddingTop: 15 }}>
                                    
                                    {testLinks.map((link, index) => (
                                        <div key={index} style={{ display: 'flex', gap: 10 }}>
                                            <input 
                                                type="text" 
                                                placeholder={index === 0 ? "Primary Commentary URL" : (index <= 2 ? `Captain ${index} URL` : `Additional Stream URL`)} 
                                                value={link}
                                                onChange={(e) => {
                                                    const newLinks = [...testLinks];
                                                    newLinks[index] = e.target.value;
                                                    setTestLinks(newLinks);
                                                }}
                                                style={{ flex: 1, background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255, 215, 0, 0.3)', color: 'white', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}
                                            />
                                            <button 
                                                onClick={() => {
                                                    const newLinks = testLinks.filter((_, i) => i !== index);
                                                    setTestLinks(newLinks.length ? newLinks : ['']); // Keep at least one empty input
                                                }}
                                                style={{ background: 'rgba(255, 92, 92, 0.1)', color: '#FF5C5C', border: 'none', padding: '0 15px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold' }}
                                                title="Remove link"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                    
                                    <button 
                                        onClick={() => setTestLinks([...testLinks, ''])}
                                        style={{ background: 'rgba(108, 93, 211, 0.2)', color: '#A395E9', border: '1px solid rgba(108, 93, 211, 0.5)', padding: '8px 15px', borderRadius: 6, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, alignSelf: 'flex-start' }}
                                    >
                                        + Add Stream Link
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                    
                    {/* --- STATE 1: ACTIVE PLAYER --- */}
                    {activePlayerEvent && (
                        <div className="GlassCard" style={{padding: 0, marginBottom: 40, width: '100%', overflow: 'hidden'}}>
                            <div style={{background: '#151823', padding: '10px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid #333'}}>
                                <span style={{fontWeight: 'bold', color: '#fff'}}>
                                    {liveEvents.find(e => e.id === activePlayerEvent.id) ? '🔴 LIVE NOW:' : '📼 VOD:'} {activePlayerEvent.title}
                                </span>
                                { /* Only allow closing if it's not currently live */ }
                                {!liveEvents.find(e => e.id === activePlayerEvent.id) && (
                                    <button onClick={() => setSelectedEvent(null)} style={{background: 'transparent', border: 'none', color: '#FF5C5C', cursor: 'pointer', fontWeight: 'bold'}}>
                                        ✕ Close
                                    </button>
                                )}
                            </div>
                            <MultiStreamPlayer event={activePlayerEvent} /> 
                        </div>
                    )}

                    {/* --- STATE 2: NO ACTIVE PLAYER, SHOW NEXT MATCH BANNER --- */}
                    {!selectedEvent && nextEvent && (
                        <div className="GlassCard" style={{ padding: '60px 20px', textAlign: 'center', marginBottom: 40, background: 'linear-gradient(145deg, #1A1C29 0%, #10121A 100%)' }}>
                            <div style={{ display: 'inline-block', background: 'rgba(108, 93, 211, 0.2)', color: '#A395E9', padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 'bold', marginBottom: 20, textTransform: 'uppercase', letterSpacing: 2 }}>
                                Next Broadcast
                            </div>
                            <h2 style={{ fontSize: 32, color: '#FFF', margin: '0 0 10px 0' }}>{nextEvent.title}</h2>
                            <div style={{ fontSize: 20, color: '#FFD700', fontWeight: 'bold', marginBottom: 5 }}>
                                {getCountdownText(nextEvent.startTime)}
                            </div>
                            <div style={{ fontSize: 15, color: '#8F9EB5' }}>
                               {getEffectiveStartTime(nextEvent.startTime).toFormat('EEEE, MMMM d @ h:mm a')}
                            </div>
                        </div>
                    )}

                    {/* --- STATE 3: ARCHIVES TOGGLE & LIST --- */}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginTop: 20 }}>
                        
                        {/* The Toggle Button (Only shows if there are actual future streams) */}
                        {(liveEvents.length > 0 || upcomingEvents.length > 0) && (
                            <button 
                                onClick={() => setShowArchives(!showArchives)}
                                style={{
                                    background: showArchives ? 'rgba(255,255,255,0.05)' : '#6C5DD3',
                                    color: showArchives ? '#8F9EB5' : 'white',
                                    border: showArchives ? '1px solid rgba(255,255,255,0.1)' : 'none',
                                    padding: '12px 30px', borderRadius: 30, fontSize: 15, fontWeight: 'bold',
                                    cursor: 'pointer', transition: 'all 0.2s', marginBottom: 30,
                                    boxShadow: showArchives ? 'none' : '0 10px 20px rgba(108, 93, 211, 0.3)'
                                }}
                            >
                                {showArchives ? 'Hide Past Streams' : '📼 Show Past streams'}
                            </button>
                        )}

                        {/* The Archive List */}
                        {showArchives && (
                            <div className="GlassCard" style={{ width: '100%', animation: 'fadeIn 0.3s ease-in', marginTop: 20, padding: 0, display: 'flex', flexDirection: 'column', maxHeight: '75vh', border: '1px solid rgba(255,255,255,0.1)' }}>
                                {/* Sticky Header */}
                                <div style={{ padding: '20px', background: 'rgba(0,0,0,0.3)', borderBottom: '1px solid rgba(255,255,255,0.1)', flexShrink: 0, zIndex: 2 }}>
                                    <h3 style={{ color: '#FFF', margin: 0, textAlign: 'center' }}>
                                        Tournament Archives
                                    </h3>
                                </div>
                                
                                {/* Scrollable Content */}
                                <div className="CustomScroll" style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                                    {pastEvents.length === 0 ? (
                                        <div style={{ textAlign: 'center', color: '#ccc', padding: 40 }}>No past broadcasts are available yet.</div>
                                    ) : (
                                        <div style={{ display: 'flex', flexDirection: 'column', gap: 15 }}>
                                        {pastEvents.map(event => {
                                            const hasLink = (event.links && event.links.length > 0) || event.link;
                                            
                                            // NEW: Check if this card is the one currently playing
                                            const isActive = selectedEvent?.id === event.id;

                                            return (
                                                <div key={event.id} id={`archive-${event.id}`} className="GlassCard" style={{ 
                                                    padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column',
                                                    // NEW: Gold border if active, standard if not
                                                    border: isActive ? '2px solid #FFD700' : '1px solid rgba(255,255,255,0.08)',
                                                    boxShadow: isActive ? '0 0 20px rgba(255, 215, 0, 0.2)' : '0 4px 20px rgba(0,0,0,0.3)',
                                                    transition: 'all 0.3s ease'
                                                }}>
                                                    
                                                    {/* Card Header: Date & Title */}
                                                    <div style={{ background: isActive ? 'rgba(255, 215, 0, 0.1)' : 'rgba(0,0,0,0.2)', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                                                        <div style={{ fontSize: 13, color: isActive ? '#FFD700' : '#8F9EB5', fontWeight: 'bold' }}>
                                                            {isActive ? '▶ NOW PLAYING' : getLocalTime(event.startTime).toFormat('MMM d, yyyy')}
                                                        </div>
                                                        <div style={{ fontSize: 14, color: '#FFF', fontWeight: 'bold' }}>
                                                            {event.title}
                                                            {event.result && <span style={{ marginLeft: 10, color: '#111', background: '#FFD700', fontSize: 11, padding: '2px 6px', borderRadius: 4 }}>{event.result}</span>}
                                                        </div>
                                                    </div>

                                                    {/* Card Body: Teams & Button */}
                                                    <div style={{ padding: '20px', display: 'flex', alignItems: 'center', gap: 20 }}>
                                                        <TeamDetails teamId={event.team1} align="left" />
                                                        
                                                        <div style={{ flexShrink: 0, textAlign: 'center' }}>
                                                            <div style={{ fontSize: 11, color: '#555', fontWeight: '900', marginBottom: 10 }}>VS</div>
                                                            {hasLink ? (
                                                                <button 
                                                                    onClick={() => {
                                                                        setSelectedEvent(event);
                                                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                                                    }}
                                                                    style={{ 
                                                                        background: isActive ? '#FFD700' : '#2D2B3F', 
                                                                        color: isActive ? '#000' : 'white', 
                                                                        border: 'none', padding: '8px 20px', borderRadius: 20, cursor: 'pointer', fontWeight: 'bold', fontSize: 12, transition: 'background 0.2s' 
                                                                    }}
                                                                >
                                                                    {isActive ? 'Playing...' : '▶ Watch VOD'}
                                                                </button>
                                                            ) : (
                                                                <div style={{ fontSize: 11, color: '#555', fontStyle: 'italic' }}>No VOD</div>
                                                            )}
                                                        </div>

                                                        <TeamDetails teamId={event.team2} align="right" />
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            </div>
                        )}
                    </div>

                </div>
            </div>
            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </div>
    );
};

export default Watch;